import { h } from "preact";
import { useState, useRef } from "preact/hooks";

import { Variable } from "../../../foundation/api/model/variables/Variables";
import classname from "../../../foundation/utils/classname";
import { pipe } from "../../../foundation/utils/legacy";
import IconDrag from "../../v2/Icons/IconDragSmall";
import { DragSource, DropTarget, DragSourceProps } from "../../DragDropContainer";

import styles from "./VariableName.module.css";

interface VariableNameItemProps {
    variable: Variable;
    reorderEnabled: boolean;
    mirror?: boolean;
    isDragging: boolean;
    dragMode: boolean;
    targetPosition?: string;
}

interface Props extends VariableNameItemProps, DragSourceProps {
    index: number;
    dragMode: boolean;
    allowDropToTop: boolean;
    isDragging: boolean;
    reorderEnabled: boolean;
    variable: Variable;
    onStartDrag: (variableId: string) => void;
    onItemDrop: ({
        sourceId, sourceIndex, moveIndex
    }: {
        sourceId: string; sourceIndex: number; moveIndex: number;
    }) => void;
}

interface DragDropInjectedProps {
    onStartDrag: () => void;
    onMirrorHover: ({ top }: { top: number; }) => void;
    onMirrorHoverOut: ({ top }: { top: number; }) => void;
    onDrop: (dropData: DropData) => void;
}

type DragDropProps = Props & DragDropInjectedProps;

interface DropData {
    sourceId: string;
    sourceIndex: number;
}

interface NodeRef {
    base: HTMLDivElement;
}

const ITEM_HEIGHT = 34;

function VariableName({
    variable,
    reorderEnabled,
    mirror,
    isDragging,
    dragMode,
    targetPosition
}: VariableNameItemProps) {
    return (
        <div
            class={classname(styles.variableName, "noSelect ellipsis", {
                [styles.mirror]: mirror,
                [styles.dragMode]: dragMode,
                [styles.isDragging]: isDragging,
                [targetPosition && styles[targetPosition]]: true
            })}>
            <span class={styles.dragHandleContainer}>
                {reorderEnabled && <IconDrag class={styles.dragHandle}/>}
            </span>
            <span class={classname(styles.name, "body-1", "ellipsis")}>
                {!isDragging && variable.name}
            </span>
            {targetPosition && <div class={styles.dropIndicator}/>}
        </div>
    );
}

const VariableNameMirror = ({ ...props }: Props) => (
    <VariableName mirror {...props}/>
);

const RawDragDropVariableName = pipe(
    DragSource<DragDropProps>({
        itemType: "VariableName",
        mirrorComponent: VariableNameMirror,
        mirrorZIndex: 1000,
        onStartDrag: props => props.onStartDrag(),
        provideDragData: props => props
    }),
    DropTarget<DragDropProps>({
        acceptsItemType: "VariableName",
        discardHorizontalPosition: true,
        onDrop: (props, dragState) => props.onDrop({
            sourceId: dragState.dragData!.variable._id,
            sourceIndex: dragState.dragData!.index
        }),
        onHover: (props, dragState) => props.onMirrorHover({
            top: dragState.mirrorPosition.top
        }),
        onHoverOut: (props, dragState) => props.onMirrorHoverOut({
            top: dragState.mirrorPosition.top
        })
    })
)(VariableName);

function DragDropVariableName(props: Props) {
    const {
        index: targetIndex,
        variable,
        allowDropToTop,
        reorderEnabled,
        onStartDrag,
        onItemDrop
    } = props;
    const variableNameItem = useRef<NodeRef>(null);
    const [targetPosition, setTargetPosition] = useState("");

    function handleDragStart() {
        onStartDrag(variable._id);
    }

    function handleMirrorHover({ top }: { top: number; }) {
        const { base } = variableNameItem.current;
        const baseRect = base.getBoundingClientRect();
        const targetToBottom = !allowDropToTop || top >= baseRect.top + ITEM_HEIGHT / 2;
        const targetPositionText = targetToBottom ? "bottom" : "top";

        if (targetPosition !== targetPositionText) {
            setTargetPosition(targetPositionText);
        }
    }

    function handleMirrorHoverOut() {
        if (targetPosition) {
            setTargetPosition("");
        }
    }

    function handleDrop(
        { sourceId, sourceIndex }:
        { sourceId: string; sourceIndex: number; }
    ) {
        if (!targetPosition) {
            return;
        }

        const targetToBottom = targetPosition === "bottom";
        let moveIndex = targetIndex;

        if (sourceIndex > targetIndex && targetToBottom) {
            moveIndex = targetIndex + 1;
        }

        if (sourceIndex < targetIndex && !targetToBottom) {
            moveIndex = targetIndex - 1;
        }

        if (sourceIndex === moveIndex) {
            return;
        }

        onItemDrop({ sourceId, sourceIndex, moveIndex });
    }

    return reorderEnabled ? (
        <RawDragDropVariableName
            {...props}
            ref={variableNameItem}
            targetPosition={targetPosition}
            onStartDrag={handleDragStart}
            onMirrorHover={handleMirrorHover}
            onMirrorHoverOut={handleMirrorHoverOut}
            onDrop={handleDrop}/>
    ) : (
        <VariableName {...props}/>
    );
}

export default DragDropVariableName;
